import { distance, type Vec3 } from './mathx';

export type TargetClass = 'drone' | 'fixedwing' | 'vehicle' | 'bird';
export type TargetPathEndBehavior = 'loop' | 'stop' | 'reverse';

/** A route point. `speed` applies to the leg arriving at this point. */
export interface TargetWaypoint {
  posEnu: Vec3;
  /** per-leg speed override, m/s */
  speed?: number;
  /** time to remain stationary on arrival, seconds */
  holdAt?: number;
}

export interface TargetPath {
  waypoints: TargetWaypoint[];
  endBehavior: TargetPathEndBehavior;
}

export interface TargetSpec {
  id: string;
  class: TargetClass;
  startEnu: Vec3;
  path: TargetPath;
  /** default ground speed, m/s */
  speed: number;
  /** radar cross-section, m²; defaults by target class */
  rcs?: number;
  /** maximum horizontal heading rate, rad/s */
  turnRateRadPerSec?: number;
}

/** Collider-free kinematic truth consumed by radar, telemetry, and camera checks. */
export interface TargetState {
  id: string;
  posEnu: Vec3;
  velEnu: Vec3;
  /** ENU yaw: zero is east, positive turns toward north. Kept unwrapped for continuity. */
  headingRad: number;
  class: TargetClass;
  rcs: number;
}

export const DEFAULT_TARGET_TURN_RATE_RAD_PER_SEC = Math.PI / 2;

export const DEFAULT_RCS_BY_CLASS: Readonly<Record<TargetClass, number>> = Object.freeze({
  bird: 0.01,
  drone: 0.1,
  fixedwing: 3,
  vehicle: 10,
});

export class TargetSpecError extends Error {
  constructor(message: string) {
    super(`target spec: ${message}`);
    this.name = 'TargetSpecError';
  }
}

const TARGET_CLASSES: readonly TargetClass[] = ['drone', 'fixedwing', 'vehicle', 'bird'];
const END_BEHAVIORS: readonly TargetPathEndBehavior[] = ['loop', 'stop', 'reverse'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const finiteNumber = (value: unknown, path: string): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new TargetSpecError(`${path} must be a finite number`);
  }
  return value;
};

const positiveNumber = (value: unknown, path: string): number => {
  const parsed = finiteNumber(value, path);
  if (parsed <= 0) throw new TargetSpecError(`${path} must be > 0`);
  return parsed;
};

const vec3Of = (value: unknown, path: string): Vec3 => {
  if (!isRecord(value)) throw new TargetSpecError(`${path} must be an object {x,y,z}`);
  return {
    x: finiteNumber(value.x, `${path}.x`),
    y: finiteNumber(value.y, `${path}.y`),
    z: finiteNumber(value.z, `${path}.z`),
  };
};

/** Validate and defensively copy a target specification loaded from scenario data. */
export const parseTargetSpec = (raw: unknown, path = 'root'): TargetSpec => {
  if (!isRecord(raw)) throw new TargetSpecError(`${path} must be an object`);
  if (typeof raw.id !== 'string' || raw.id.length === 0) {
    throw new TargetSpecError(`${path}.id must be a non-empty string`);
  }
  if (!TARGET_CLASSES.includes(raw.class as TargetClass)) {
    throw new TargetSpecError(`${path}.class must be drone, fixedwing, vehicle, or bird`);
  }
  if (!isRecord(raw.path)) throw new TargetSpecError(`${path}.path must be an object`);
  if (!Array.isArray(raw.path.waypoints) || raw.path.waypoints.length === 0) {
    throw new TargetSpecError(`${path}.path.waypoints must be a non-empty array`);
  }
  if (!END_BEHAVIORS.includes(raw.path.endBehavior as TargetPathEndBehavior)) {
    throw new TargetSpecError(`${path}.path.endBehavior must be loop, stop, or reverse`);
  }

  const waypoints = raw.path.waypoints.map((value, index): TargetWaypoint => {
    const waypointPath = `${path}.path.waypoints[${index}]`;
    if (!isRecord(value)) throw new TargetSpecError(`${waypointPath} must be an object`);
    const speed =
      value.speed === undefined ? undefined : positiveNumber(value.speed, `${waypointPath}.speed`);
    const holdAt =
      value.holdAt === undefined ? undefined : finiteNumber(value.holdAt, `${waypointPath}.holdAt`);
    if (holdAt !== undefined && holdAt < 0) {
      throw new TargetSpecError(`${waypointPath}.holdAt must be >= 0`);
    }
    return { posEnu: vec3Of(value.posEnu, `${waypointPath}.posEnu`), speed, holdAt };
  });

  const rcs = raw.rcs === undefined ? undefined : finiteNumber(raw.rcs, `${path}.rcs`);
  if (rcs !== undefined && rcs < 0) throw new TargetSpecError(`${path}.rcs must be >= 0`);

  return {
    id: raw.id,
    class: raw.class as TargetClass,
    startEnu: vec3Of(raw.startEnu, `${path}.startEnu`),
    path: {
      waypoints,
      endBehavior: raw.path.endBehavior as TargetPathEndBehavior,
    },
    speed: positiveNumber(raw.speed, `${path}.speed`),
    rcs,
    turnRateRadPerSec:
      raw.turnRateRadPerSec === undefined
        ? DEFAULT_TARGET_TURN_RATE_RAD_PER_SEC
        : positiveNumber(raw.turnRateRadPerSec, `${path}.turnRateRadPerSec`),
  };
};

/** Route length including the closing leg for loop paths. */
export const targetRouteLength = (spec: TargetSpec): number => {
  let total = 0;
  let previous = spec.startEnu;
  for (const waypoint of spec.path.waypoints) {
    total += distance(previous, waypoint.posEnu);
    previous = waypoint.posEnu;
  }
  if (spec.path.endBehavior === 'loop') total += distance(previous, spec.startEnu);
  return total;
};
